"use client";
import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  TextField,
  Button,
  Divider,
  Snackbar,
  Alert,
  Autocomplete,
  Chip,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SendIcon from "@mui/icons-material/Send";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import PersonIcon from "@mui/icons-material/Person";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useLanguage } from "./LanguageProvider";
import { useNavigate } from "react-router-dom";

export const ContactPage = () => {
  const navigate = useNavigate();
  const { currentLang, isInitialized } = useLanguage();
  const isRTL = currentLang === "ar";
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    countryCode: "+971",
    phone: "",
    subject: "",
    message: "",
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  // Country codes with Arabic names
  const countryCodes = [
    { code: "+971", flag: "🇦🇪", country: "UAE", countryAr: "الإمارات" },
    { code: "+966", flag: "🇸🇦", country: "Saudi Arabia", countryAr: "السعودية" },
    { code: "+20", flag: "🇪🇬", country: "Egypt", countryAr: "مصر" },
    { code: "+974", flag: "🇶🇦", country: "Qatar", countryAr: "قطر" },
    { code: "+973", flag: "🇧🇭", country: "Bahrain", countryAr: "البحرين" },
    { code: "+968", flag: "🇴🇲", country: "Oman", countryAr: "عمان" },
    { code: "+965", flag: "🇰🇼", country: "Kuwait", countryAr: "الكويت" },
    { code: "+962", flag: "🇯🇴", country: "Jordan", countryAr: "الأردن" },
    { code: "+961", flag: "🇱🇧", country: "Lebanon", countryAr: "لبنان" },
    { code: "+964", flag: "🇮🇶", country: "Iraq", countryAr: "العراق" },
    { code: "+967", flag: "🇾🇪", country: "Yemen", countryAr: "اليمن" },
    { code: "+1", flag: "🇺🇸", country: "USA/Canada", countryAr: "أمريكا/كندا" },
    { code: "+44", flag: "🇬🇧", country: "UK", countryAr: "بريطانيا" },
    { code: "+91", flag: "🇮🇳", country: "India", countryAr: "الهند" },
    { code: "+92", flag: "🇵🇰", country: "Pakistan", countryAr: "باكستان" },
  ];

  // Translations
  const translations = {
    en: {
      headerTitle: "Contact Us",
      headerDesc: "We'd love to hear from you. Get in touch with our team today.",
      companyInfoTitle: "Company Information",
      getInTouch: "Get in Touch",
      headOfLegal: "HEAD OF LEGAL DEPARTMENT",
      phone: "PHONE",
      email: "EMAIL",
      headOffice: "HEAD OFFICE",
      workingHours: "WORKING HOURS",
      workingHoursValue: "Mon - Fri: 9:00 AM - 6:00 PM",
      whatsapp: "WhatsApp",
      linkedin: "LinkedIn",
      formTitle: "Send Us a Message",
      formDesc: "Fill out the form below and we'll get back to you within 24 hours",
      fullName: "Full Name",
      emailAddress: "Email Address",
      countryCode: "Country Code",
      phoneNumber: "Phone Number",
      subject: "Subject",
      message: "How can we help you?",
      sendButton: "Send Message Now",
      sending: "Sending...",
      successMessage: "Your message has been sent successfully!",
      errorMessage: "An error occurred. Please try again.",
      backToHome: "Back to Home",
    },
    ar: {
      headerTitle: "اتصل بنا",
      headerDesc: "يسعدنا التواصل معك. تواصل مع فريقنا اليوم.",
      companyInfoTitle: "معلومات الشركة",
      getInTouch: "تواصل معنا",
      headOfLegal: "رئيس القسم القانوني",
      phone: "الهاتف",
      email: "البريد الإلكتروني",
      headOffice: "المكتب الرئيسي",
      workingHours: "ساعات العمل",
      workingHoursValue: "الإثنين - الجمعة: 9 ص - 6 م",
      whatsapp: "واتساب",
      linkedin: "لينكد إن",
      formTitle: "أرسل لنا رسالة",
      formDesc: "املأ النموذج أدناه وسنرد عليك خلال 24 ساعة",
      fullName: "الاسم الكامل",
      emailAddress: "البريد الإلكتروني",
      countryCode: "كود الدولة",
      phoneNumber: "رقم الهاتف",
      subject: "الموضوع",
      message: "كيف يمكننا مساعدتك؟",
      sendButton: "إرسال الرسالة الآن",
      sending: "جاري الإرسال...",
      successMessage: "تم إرسال رسالتك بنجاح!",
      errorMessage: "حدث خطأ. حاول مرة أخرى.",
      backToHome: "الرجوع للرئيسية",
    },
  };

  const t = translations[currentLang];

  const companyInfo = [
    { icon: PersonIcon, title: t.headOfLegal, value: "Hatem Elsegai" },
    { icon: PhoneIcon, title: t.phone, value: "+971 50 333 0841" },
    { icon: EmailIcon, title: t.email, value: "info@ruknalmurooj.com" },
    { icon: LocationOnIcon, title: t.headOffice, value: "Business Bay, Dubai, UAE" },
    { icon: AccessTimeIcon, title: t.workingHours, value: t.workingHoursValue },
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle form submit with API integration
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Prepare data according to API format
    const submissionData = {
      name: formData.name,
      email: formData.email,
      phonecode: formData.countryCode,
      phonenumber: formData.phone,
      subject: formData.subject,
      message: formData.message,
    };

    try {
      const response = await fetch("https://crmapitest4.onrender.com/api/v1/clients", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submissionData),
      });

      const data = await response.json();

      if (response.ok) {
        // Success
        setSnackbar({
          open: true,
          message: t.successMessage,
          severity: "success",
        });
        // Reset form
        setFormData({
          name: "",
          email: "",
          countryCode: "+971",
          phone: "",
          subject: "",
          message: "",
        });
      } else {
        // Server returned an error
        throw new Error(data.message || "Server error");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      setSnackbar({
        open: true,
        message: t.errorMessage,
        severity: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const fieldStyle = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: "#f9f9f9",
      transition: "0.3s",
      "& fieldset": { borderColor: "#e0e0e0" },
      "&:hover fieldset": { borderColor: "#d4af37" },
      "&.Mui-focused fieldset": { borderColor: "#d4af37" },
    },
    "& .MuiInputLabel-root": { 
      fontFamily: "'Poppins', 'Roboto', sans-serif",
      fontWeight: 500,
    },
  };

  if (!isInitialized) {
    return null;
  }

  return (
    <Box sx={{
      minHeight: "100vh",
      py: { xs: 8, md: 12 },
      background: "linear-gradient(135deg, #f5f7fa 0%, #e9eef5 100%)",
      pt: { xs: 12, md: 14 },
    }}>
      <Container maxWidth="lg">
        {/* Back Button */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ mb: 4 }}>
            <Button
              onClick={() => navigate("/")}
              sx={{
                color: "#c9a03d",
                borderColor: "#c9a03d",
                borderRadius: "30px",
                px: 3,
                "&:hover": {
                  bgcolor: "#c9a03d",
                  color: "white",
                },
              }}
              variant="outlined"
              startIcon={isRTL ? null : <ArrowBackIcon />}
              endIcon={isRTL ? <ArrowBackIcon /> : null}
            >
              {t.backToHome}
            </Button>
          </Box>
        </motion.div>

        {/* Header Section */}
        <motion.div
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Box sx={{ textAlign: "center", mb: { xs: 4, sm: 6, md: 8 } }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontFamily: "'Poppins', 'Roboto', sans-serif", 
                fontWeight: 800, 
                color: "#1a1a2e", 
                mb: 2,
                fontSize: { xs: "1.8rem", sm: "2.5rem", md: "3rem", lg: "3.5rem" },
              }}
            >
              {t.headerTitle}
            </Typography>
            <Box sx={{ width: { xs: 60, sm: 80 }, height: 4, bgcolor: "#d4af37", mx: "auto", borderRadius: 2 }} />
            <Typography 
              variant="body1" 
              sx={{ 
                color: "#666", 
                mt: 2, 
                maxWidth: 600, 
                mx: "auto", 
                px: 2,
                fontSize: { xs: "0.85rem", sm: "1rem" }
              }}
            >
              {t.headerDesc}
            </Typography>
          </Box>
        </motion.div>

        {/* Two Cards */}
        <Grid 
          container 
          spacing={{ xs: 3, sm: 3, md: 4 }} 
          sx={{ 
            textAlign: "center",
            margin: "0 auto",
          }}
        >
          {/* Company Info Card */}
          <Grid item xs={12} md={5}>
            <motion.div
              initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              style={{ height: "100%" }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 3.5, md: 4 },
                  height: "100%",
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: { xs: "20px", sm: "24px" },
                  background: "white",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                  border: "1px solid rgba(212,175,55,0.2)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.75rem" },
                    fontWeight: 700,
                    color: "#1a1a2e",
                    fontFamily: "'Poppins', sans-serif",
                    mb: 2,
                    textAlign: "center"
                  }}
                >
                  {t.companyInfoTitle}
                </Typography>

                <Divider sx={{ mb: 2.5 }}>
                  <Chip 
                    label={t.getInTouch} 
                    sx={{ 
                      fontFamily: "'Poppins', sans-serif", 
                      fontWeight: 600,
                      bgcolor: "#d4af37",
                      color: "#1a1a2e",
                      fontSize: { xs: "0.7rem", sm: "0.75rem" }
                    }} 
                  />
                </Divider>

                <Stack spacing={{ xs: 2, sm: 2.5 }}>
                  {companyInfo.map((info, index) => (
                    <Box
                      key={index}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        flexDirection: isRTL ? "row-reverse" : "row",
                        p: { xs: 1, sm: 1.5 },
                        borderRadius: "12px",
                        transition: "0.3s",
                        "&:hover": {
                          bgcolor: "#f8f9fa",
                          transform: { xs: "translateX(0px)", sm: isRTL ? "translateX(-5px)" : "translateX(5px)" }
                        }
                      }}
                    >
                      <Box
                        sx={{
                          width: { xs: 40, sm: 45 },
                          height: { xs: 40, sm: 45 },
                          borderRadius: "12px",
                          backgroundColor: "rgba(212, 175, 55, 0.1)",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0
                        }}
                      >
                        <info.icon sx={{ color: "#d4af37", fontSize: { xs: 22, sm: 24 } }} />
                      </Box>
                      <Box sx={{ flex: 1, textAlign: isRTL ? "right" : "left" }}>
                        <Typography
                          sx={{
                            fontSize: { xs: "9px", sm: "10px", md: "11px" },
                            fontWeight: 600,
                            letterSpacing: "1px",
                            color: "#9aa6b5",
                            mb: 0.5,
                            textTransform: "uppercase",
                            fontFamily: "'Poppins', sans-serif"
                          }}
                        >
                          {info.title}
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: { xs: "12px", sm: "13px", md: "14px" },
                            fontWeight: 600,
                            color: "#1e293b",
                            fontFamily: "'Poppins', sans-serif",
                            wordBreak: "break-word"
                          }}
                        >
                          {info.value}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                <Box sx={{ mt: "auto", pt: 3 }}>
                  <Divider sx={{ mb: 2.5 }} />
                  <Stack direction="row" spacing={2} justifyContent="center">
                    <Button 
                      href="https://wa.me/971503330841" 
                      target="_blank"
                      startIcon={<WhatsAppIcon />} 
                      sx={{ 
                        color: "#25D366", 
                        borderColor: "#25D366", 
                        borderRadius: "10px", 
                        fontFamily: "'Poppins', sans-serif",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                        px: { xs: 2, sm: 3 },
                        "&:hover": { 
                          bgcolor: "#25D366", 
                          color: "white",
                          borderColor: "#25D366"
                        }
                      }} 
                      variant="outlined"
                    >
                      {t.whatsapp}
                    </Button>
                    <Button 
                      href="#" 
                      startIcon={<LinkedInIcon />} 
                      sx={{ 
                        color: "#0A66C2", 
                        borderColor: "#0A66C2", 
                        borderRadius: "10px", 
                        fontFamily: "'Poppins', sans-serif",
                        textTransform: "none",
                        fontWeight: 600,
                        fontSize: { xs: "0.75rem", sm: "0.85rem" },
                        px: { xs: 2, sm: 3 },
                        "&:hover": { 
                          bgcolor: "#0A66C2", 
                          color: "white",
                          borderColor: "#0A66C2"
                        }
                      }} 
                      variant="outlined"
                    >
                      {t.linkedin}
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            </motion.div>
          </Grid>

          {/* Contact Form Card */}
          <Grid item xs={12} md={7}>
            <motion.div
              initial={{ opacity: 0, x: isRTL ? -30 : 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              style={{ height: "100%" }}
            >
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 3, sm: 3.5, md: 4 },
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderRadius: { xs: "20px", sm: "24px" },
                  background: "white",
                  boxShadow: "0 20px 40px rgba(0,0,0,0.1)",
                }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: "1.3rem", sm: "1.5rem", md: "1.75rem" },
                    fontWeight: 700,
                    color: "#1a1a2e",
                    fontFamily: "'Poppins', sans-serif",
                    mb: 2,
                    textAlign: "center"
                  }}
                >
                  {t.formTitle}
                </Typography>

                <Typography 
                  variant="body2" 
                  sx={{ 
                    textAlign: "center", 
                    mb: 2.5, 
                    color: "#666",
                    fontSize: { xs: "0.75rem", sm: "0.8rem", md: "0.875rem" }
                  }}
                >
                  {t.formDesc}
                </Typography>

                <form onSubmit={handleSubmit} style={{ flex: 1 }}>
                  <Stack spacing={{ xs: 2, sm: 2.5 }}>
                    <TextField 
                      fullWidth 
                      label={t.fullName} 
                      name="name" 
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      sx={fieldStyle}
                      dir={isRTL ? "rtl" : "ltr"}
                    />
                    
                    <TextField 
                      fullWidth 
                      label={t.emailAddress} 
                      name="email" 
                      type="email" 
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      sx={fieldStyle}
                      dir={isRTL ? "rtl" : "ltr"}
                    />

                    <Box sx={{ display: "flex", gap: 2, flexDirection: { xs: "column", sm: "row" } }}>
                      <Autocomplete
                        options={countryCodes}
                        getOptionLabel={(option) => currentLang === "ar" 
                          ? `${option.flag} ${option.code} (${option.countryAr})`
                          : `${option.flag} ${option.code} (${option.country})`
                        }
                        disableClearable
                        sx={{ width: { xs: "100%", sm: 180 } }}
                        renderInput={(params) => <TextField {...params} label={t.countryCode} sx={fieldStyle} />}
                        defaultValue={countryCodes[0]}
                        onChange={(e, newValue) => setFormData(prev => ({ ...prev, countryCode: newValue.code }))}
                      />
                      <TextField 
                        fullWidth 
                        label={t.phoneNumber} 
                        name="phone" 
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                        sx={fieldStyle}
                        dir={isRTL ? "rtl" : "ltr"}
                      />
                    </Box>

                    <TextField 
                      fullWidth 
                      label={t.subject} 
                      name="subject" 
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      sx={fieldStyle}
                      dir={isRTL ? "rtl" : "ltr"}
                    />

                    <TextField 
                      fullWidth 
                      multiline 
                      rows={4} 
                      label={t.message} 
                      name="message" 
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      sx={fieldStyle}
                      dir={isRTL ? "rtl" : "ltr"}
                    />

                    <Button
                      fullWidth
                      type="submit"
                      variant="contained"
                      disabled={isSubmitting}
                      endIcon={<SendIcon />}
                      sx={{
                        py: { xs: 1.5, sm: 1.8 },
                        borderRadius: "12px",
                        bgcolor: "#1a1a2e",
                        fontFamily: "'Poppins', sans-serif",
                        fontWeight: 700,
                        fontSize: { xs: "0.85rem", sm: "0.9rem" },
                        textTransform: "none",
                        "&:hover": { 
                          bgcolor: "#d4af37", 
                          color: "#1a1a2e"
                        },
                      }}
                    >
                      {isSubmitting ? t.sending : t.sendButton}
                    </Button>
                  </Stack>
                </form>
              </Paper>
            </motion.div>
          </Grid>
        </Grid>
      </Container>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert 
          severity={snackbar.severity} 
          variant="filled" 
          sx={{ 
            width: '100%', 
            fontFamily: "'Poppins', sans-serif", 
            borderRadius: "12px"
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactPage;